/**
 * Azure Chunked Upload Module
 * 
 * Uses the official Azure Storage Blob SDK for reliable chunked uploads.
 * Files larger than 256MB are automatically uploaded using Azure's Block Blob API
 * with proper chunking, retry logic, and error handling.
 */

import { DirectUpload } from '@rails/activestorage';
import { BlockBlobClient } from '@azure/storage-blob';

const MAX_SINGLE_PUT_SIZE = 256 * 1024 * 1024; // 256MB Azure limit
const DIRECT_UPLOAD_URL = '/rails/active_storage/direct_uploads';

/**
 * Upload a file using chunked upload for files > 256MB
 * Falls back to regular direct upload for smaller files
 */
export async function uploadFileWithChunking(file, options = {}) {
  const uploadEndpoint = options.directUploadUrl || options.url || DIRECT_UPLOAD_URL;
  const uploadOptions = { ...options, directUploadUrl: uploadEndpoint };
  const { onProgress } = uploadOptions;

  // For files <= 256MB, use regular direct upload
  if (file.size <= MAX_SINGLE_PUT_SIZE) {
    return uploadFileDirectly(file, uploadOptions);
  }

  console.log(`[ChunkedUpload] File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds 256MB limit, using Azure SDK for chunked upload`);

  try {
    // Step 1: Create blob record on server and get SAS URL
    const blobData = await createBlobRecord(file, uploadEndpoint);
    const uploadUrl = blobData.direct_upload.url;

    // Verify we got a valid upload URL
    if (!uploadUrl) {
      throw new Error('No upload URL returned from server');
    }

    // Verify the URL has a SAS token with required permissions
    try {
      const urlObj = new URL(uploadUrl);
      const params = new URLSearchParams(urlObj.search);
      const permissions = params.get('sp') || '';
      const resource = params.get('sr') || '';
      
      if (!permissions.includes('c')) {
        console.warn('[ChunkedUpload] Warning: SAS token may not have "create" permission. Got sp=' + permissions);
      }
      
      if (resource !== 'b') {
        console.warn('[ChunkedUpload] Warning: SAS token resource type is not "b" (blob). Got sr=' + resource);
      }
      
      console.log('[ChunkedUpload] Verified upload URL:', {
        hasAzureDomain: uploadUrl.includes('.blob.core.windows.net'),
        permissions: permissions,
        resource: resource,
        hasSignature: params.has('sig')
      });
    } catch (e) {
      console.warn('[ChunkedUpload] Could not parse upload URL for validation:', e);
    }

    // Step 2: Use Azure SDK to upload with automatic chunking
    const blockBlobClient = new BlockBlobClient(uploadUrl);

    console.log(`[ChunkedUpload] Starting upload using Azure SDK...`);
    console.log(`[ChunkedUpload] File: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
    console.log(`[ChunkedUpload] Upload URL: ${uploadUrl.substring(0, 100)}...`);

    // Upload with automatic chunking (Azure SDK handles this)
    await blockBlobClient.uploadData(file, {
      blockSize: 4 * 1024 * 1024, // 4MB chunks (Azure's recommended size)
      concurrency: 5, // Upload 5 chunks concurrently for better performance
      onProgress: (progress) => {
        if (onProgress) {
          const percent = (progress.loadedBytes / file.size) * 100;
          onProgress(percent);
          console.log(`[ChunkedUpload] Progress: ${percent.toFixed(1)}% (${(progress.loadedBytes / 1024 / 1024).toFixed(2)}MB / ${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        }
      },
      blobHTTPHeaders: {
        blobContentType: file.type || 'application/octet-stream',
        blobContentDisposition: `inline; filename="${file.name}"`
      }
    }).catch((error) => {
      console.error('[ChunkedUpload] Azure SDK upload error:', error);
      console.error('[ChunkedUpload] Error details:', {
        name: error.name,
        message: error.message,
        statusCode: error.statusCode,
        code: error.code,
        requestId: error.requestId,
        response: error.response
      });
      
      // Check for CORS errors specifically
      const errorMessage = error.message || '';
      const isCorsError = errorMessage.includes('CORS') || 
                         errorMessage.includes('Access-Control') ||
                         errorMessage.includes('cross-origin') ||
                         error.statusCode === 0; // CORS errors often return status 0
      
      if (isCorsError) {
        console.error('[ChunkedUpload] CORS Error Detected!');
        console.error('[ChunkedUpload] Current origin:', window.location.origin);
        console.error('[ChunkedUpload] Upload URL:', uploadUrl.substring(0, 150));
        console.error('[ChunkedUpload] Make sure your origin is in Azure CORS allowed origins');
        console.error('[ChunkedUpload] Run: docker compose exec app bundle exec rails azure:cors:verify');
        throw new Error(`CORS error: Upload blocked by browser. Ensure your origin (${window.location.origin}) is in Azure Storage CORS allowed origins. Check browser console for details.`);
      }
      
      // Provide more helpful error messages
      if (error.statusCode === 403) {
        throw new Error('Upload failed: Authentication error (403). Check SAS token permissions and CORS configuration.');
      } else if (error.statusCode === 404) {
        throw new Error('Upload failed: Blob or container not found (404). Verify the upload URL is correct.');
      }
      
      throw error;
    });

    console.log(`[ChunkedUpload] Upload completed successfully using Azure SDK`);

    return {
      id: blobData.id,
      key: blobData.key,
      signed_id: blobData.signed_id,
      filename: blobData.filename,
      content_type: blobData.content_type,
      byte_size: blobData.byte_size,
      checksum: blobData.checksum,
      created_at: blobData.created_at,
      display_url: blobData.display_url,
      url: blobData.display_url
    };
  } catch (error) {
    console.error('[ChunkedUpload] Error during chunked upload:', error);
    throw error;
  }
}

/**
 * Parse Azure Storage URL to extract container and blob name
 * Format: https://account.blob.core.windows.net/container/blob?SAS_TOKEN
 */
function parseAzureUrl(url) {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/').filter(p => p);
    
    if (pathParts.length < 2) {
      return null;
    }

    return {
      account: urlObj.hostname.split('.')[0],
      container: pathParts[0],
      blobName: pathParts.slice(1).join('/'),
      sasToken: urlObj.search
    };
  } catch (e) {
    console.error('[ChunkedUpload] Failed to parse Azure URL:', e);
    return null;
  }
}

/**
 * Create blob record on server and get upload URL
 */
async function createBlobRecord(file, directUploadUrl = DIRECT_UPLOAD_URL) {
  const checksum = await calculateChecksum(file);

  const response = await fetch(directUploadUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCSRFToken(),
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      blob: {
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        byte_size: file.size,
        checksum: checksum
      }
    })
  });

  if (!response.ok) {
    let errorMessage = `Failed to create blob record: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.error || errorData.message) {
        errorMessage += ` - ${errorData.error || errorData.message}`;
      }
    } catch (e) {
      // If response isn't JSON, try to get text
      try {
        const errorText = await response.text();
        if (errorText) {
          errorMessage += ` - ${errorText.substring(0, 200)}`;
        }
      } catch (e2) {
        // Ignore
      }
    }
    throw new Error(errorMessage);
  }

  return await response.json();
}

/**
 * Calculate checksum for file
 * For large files (>256MB), we use a placeholder checksum since calculating
 * MD5 upfront would be slow. The server will accept a placeholder for chunked uploads.
 */
async function calculateChecksum(file) {
  // For small files, we could calculate MD5, but Active Storage's DirectUpload
  // handles this automatically for regular uploads.
  // For large files (chunked uploads), we'll use a placeholder that the server
  // will accept and replace after upload completes.
  if (file.size > MAX_SINGLE_PUT_SIZE) {
    // Return empty string - server will generate placeholder checksum
    return '';
  }

  // For small files, return empty string - Active Storage will calculate it
  // during the upload process
  return '';
}

/**
 * Get CSRF token from meta tag
 */
function getCSRFToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

/**
 * Regular direct upload for files <= 256MB
 */
async function uploadFileDirectly(file, options = {}) {
  const directUploadUrl = options.directUploadUrl || options.url || DIRECT_UPLOAD_URL;

  return new Promise((resolve, reject) => {
    const delegate = {
      directUploadWillStoreFileWithXHR: (request) => {
        if (options.onProgress) {
          request.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const progress = (event.loaded / event.total) * 100;
              options.onProgress(progress);
            }
          });
        }
      }
    };

    const directUpload = new DirectUpload(file, directUploadUrl, delegate);

    directUpload.create(async (error, blob) => {
      if (error) {
        reject(error);
      } else {
        // Fetch display URL if available
        const displayUrl = blob.display_url || await fetchDisplayUrl(blob.signed_id);

        resolve({
          id: blob.id,
          key: blob.key,
          signed_id: blob.signed_id,
          filename: blob.filename,
          content_type: blob.content_type,
          byte_size: blob.byte_size,
          checksum: blob.checksum,
          created_at: blob.created_at,
          display_url: displayUrl,
          url: displayUrl
        });
      }
    });
  });
}

/**
 * Fetch display URL for blob
 */
async function fetchDisplayUrl(signedId) {
  try {
    const response = await fetch(`/signed_blob_urls?signed_id=${encodeURIComponent(signedId)}`, {
      headers: {
        'Accept': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      return data.url;
    }
  } catch (error) {
    console.warn('[ChunkedUpload] Failed to fetch display URL:', error);
  }
  return null;
}

export { MAX_SINGLE_PUT_SIZE };
